import isEqual from 'lodash/isEqual'
import merge from 'lodash/merge'
import {useEffect, useMemo, useState} from 'react'

import {useJobId} from '../../../../EditPipelinePage/EditPipelinePage.hooks'
import {usePipelineRunArg} from '../../../PipelineRunPage.hooks'

import {isReused} from 'src/components/pages/BuildPage/DependenciesTab/DependenciesChain/DependenciesChain.utils'
import {useAppDispatch} from 'src/hooks/react-redux'
import useMyId from 'src/hooks/useMyId'
import {getBuildStatusIconFromBuild} from 'src/selectors'
import type {Build} from 'src/services/rest'
import {restApi} from 'src/services/rest'
import type {BuildId, NormalizedBuild} from 'src/types'
import {emptyArray} from 'src/utils/empty'
import {subscribeOnRunningBuild} from 'src/utils/subscriber'

function useJobParallelRuns(job?: Build) {
  const jobProjectInternalId = job?.buildType?.project?.internalId
  const jobSnapshotDependencies = job?.['snapshot-dependencies']?.build
  const isParallelized = job?.parallelized
  const isRunning = job?.state === 'running'
  const jobId = job?.id

  const arg = usePipelineRunArg()
  const dispatch = useAppDispatch()

  const parallelTestsRuns = useMemo(() => {
    if (!isParallelized || jobSnapshotDependencies == null) {
      return emptyArray as Build[]
    }
    return jobSnapshotDependencies.filter(
      dep => jobProjectInternalId !== dep.buildType?.project?.internalId,
    )
  }, [jobSnapshotDependencies, jobProjectInternalId, isParallelized])
  const [parallelTestsRunsIds, setParallelTestsRunsIds] = useState<BuildId[]>([])

  useEffect(() => {
    setParallelTestsRunsIds(prevState => {
      const newIds = parallelTestsRuns.map(parallelRun => parallelRun.id!)

      return isEqual(prevState, newIds) ? prevState : newIds
    })
  }, [parallelTestsRuns])

  useEffect(() => {
    if (isRunning) {
      const subscriptions = parallelTestsRunsIds.map(parallelRunId =>
        subscribeOnRunningBuild(parallelRunId, data => {
          dispatch(
            restApi.util.updateQueryData('getBuildNormalized', arg, draft => {
              const draftParallelRun = draft?.entities?.builds?.[draft?.result!]?.[
                'snapshot-dependencies'
              ]?.build
                ?.find(build => build.id === jobId)
                ?.['snapshot-dependencies']?.build?.find(build => build.id === parallelRunId)

              merge(draftParallelRun, data)
            }),
          )
        }),
      )

      return () => {
        subscriptions.forEach(unsubscribe => unsubscribe())
      }
    }
    return undefined
  }, [arg, dispatch, jobId, parallelTestsRunsIds, isRunning])

  return parallelTestsRuns
}

export function usePipelineRunJob(jobId: BuildId) {
  const arg = usePipelineRunArg()
  const {job, run} = restApi.endpoints.getBuildNormalized.useQuery(arg, {
    selectFromResult: ({data}) => {
      const run = data?.entities?.builds?.[data?.result!]

      return {
        job: run?.['snapshot-dependencies']?.build?.find(build => build.id === jobId),
        run,
      }
    },
  })

  const myId = useMyId()
  const icon = getBuildStatusIconFromBuild(job, myId)
  const selectedJobId = Number(useJobId())
  const isJobReused = isReused(job as NormalizedBuild, run)
  const isRunning = job?.state === 'running'
  const dispatch = useAppDispatch()

  const parallelTestsRuns = useJobParallelRuns(job)

  useEffect(() => {
    if (isRunning) {
      return subscribeOnRunningBuild(jobId, data =>
        dispatch(
          restApi.util.updateQueryData('getBuildNormalized', arg, draft => {
            const draftRun = draft?.entities?.builds?.[draft?.result!]?.[
              'snapshot-dependencies'
            ]?.build?.find(build => build.id === jobId)

            merge(draftRun, data)
          }),
        ),
      )
    }
    return undefined
  }, [arg, dispatch, jobId, isRunning])

  return {
    job,
    icon,
    isJobReused,
    selectedJobId,
    parallelTestsRuns,
  }
}
